import axios, {
  CanceledError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { UserInfo } from "@/types/common";
import { localAuthRepository } from "../repository/localAuthRepository";
import {
  memberSessionChangedEvent,
  type MemberSession,
} from "../repository/memberSessionStorage";
import { getPublicApiBaseUrl } from "@/lib/apiBaseUrl";
import { isPublicNoRedirectPath } from "@/lib/publicPaths";

const getBaseURL = () =>
  typeof window === "undefined"
    ? process.env.API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_URL ||
      "http://localhost:3001"
    : getPublicApiBaseUrl();
const httpDao = axios.create({ baseURL: getBaseURL(), withCredentials: true });

type RequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _isMutationTracked?: boolean;
  _memberRevision?: string | null;
  _memberCleanup?: () => void;
  _staffRevision?: string;
  _staffRecovery?: { revision: string; token?: string };
};

/** Match route segments, never a query string or the staff /parents resource. */
export function httpAuthScope(url = ""): "member" | "staff" | "public" {
  let path: string;
  try {
    const base = new URL(getBaseURL());
    const resolved = new URL(url, base);
    if (resolved.origin !== base.origin) return "public";
    path = resolved.pathname;
    const prefix = base.pathname.replace(/\/$/, "");
    if (prefix && path.startsWith(prefix + "/"))
      path = path.slice(prefix.length);
  } catch {
    return "public";
  }
  if (
    /^\/auth\/(member|parent)\/(login|refresh|verify-stored-session|password-reset(?:\/.*)?)\/?$/.test(
      path
    ) ||
    /^\/public(?:\/|$)/.test(path) ||
    path === "/direct-debit/config" ||
    /^\/direct-debit\/mock-registration(?:\/|$)/.test(path)
  )
    return "public";
  if (/^\/(?:auth\/(?:member|parent)|members\/me|parent)(?:\/|$)/.test(path))
    return "member";
  return "staff";
}
const pathname = (url = "") => {
  const base = new URL(getBaseURL());
  const path = new URL(url, base).pathname;
  const prefix = base.pathname.replace(/\/$/, "");
  return prefix && path.startsWith(prefix + "/")
    ? path.slice(prefix.length)
    : path;
};
const isAuthRequest = (url?: string) =>
  /^\/auth\/(staff|member|parent)\/(login|refresh|logout|verify-stored-session|password-reset|2fa)(?:\/|$)/.test(
    pathname(url)
  );
const isUnauthorized = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 401;
const stale = () =>
  new CanceledError("ログイン状態が変更されたため処理を中止しました");

let activeMutationCount = 0;
const dispatch = (name: string) => {
  if (typeof window !== "undefined")
    window.dispatchEvent(new CustomEvent(name));
};
function finish(config?: RequestConfig) {
  config?._memberCleanup?.();
  if (!config?._isMutationTracked) return;
  config._isMutationTracked = false;
  activeMutationCount = Math.max(0, activeMutationCount - 1);
  if (!activeMutationCount) dispatch("app:navigation-settle");
}
function clearStaffSession() {
  localAuthRepository.clearAll();
  if (
    typeof window !== "undefined" &&
    !isPublicNoRedirectPath(window.location.pathname)
  )
    window.location.href = "/";
}

let legacyPromise: Promise<void> | null = null;
/** One-time, server-verified migration. No staff credential is sent to this endpoint. */
export async function ensureMemberSession(): Promise<void> {
  if (localAuthRepository.member.read().revision !== null) return;
  const legacy = localAuthRepository.getLegacyMemberCredentials();
  if (!legacy) return;
  if (!legacyPromise) {
    legacyPromise = (async () => {
      try {
        const response = await httpDao.post<{ id: string }>(
          "/auth/member/verify-stored-session",
          legacy
        );
        const current = localAuthRepository.getLegacyMemberCredentials();
        if (
          current?.accessToken !== legacy.accessToken ||
          current.refreshToken !== legacy.refreshToken
        )
          throw stale();
        if (
          !localAuthRepository.member.save(
            { ...legacy, accountId: response.data.id },
            null
          )
        )
          throw stale();
        localAuthRepository.discardLegacyMemberCredentials(legacy);
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          error.response?.status === 401 &&
          error.response.data?.code === "STORED_MEMBER_SESSION_INVALID"
        ) {
          const current = localAuthRepository.getLegacyMemberCredentials();
          if (
            current?.accessToken === legacy.accessToken &&
            current.refreshToken === legacy.refreshToken &&
            localAuthRepository.member.clear(null)
          )
            localAuthRepository.discardLegacyMemberCredentials(legacy);
        }
        throw error;
      }
    })().finally(() => {
      legacyPromise = null;
    });
  }
  await legacyPromise;
}

let staffRefreshPromise: Promise<string> | null = null;
const staffRecoveries = new Map<string, Promise<UserInfo | null>>();
/** Only read-only identity verification may cross a stale tab's request fence. */
export function recoverStaffSession(): Promise<UserInfo | null> {
  const identity = localAuthRepository.getStaffRecoveryIdentity();
  if (!identity) return Promise.resolve(null);
  const revision = localAuthRepository.getStaffSessionRevision();
  const pending = staffRecoveries.get(revision);
  if (pending) return pending;
  const recovery = (async () => {
    try {
      const refreshed = await httpDao.post<{ accessToken: string }>(
        "/auth/staff/refresh",
        { expectedStaffUserId: identity.id },
        {
          timeout: 15000,
          _staffRecovery: { revision },
          headers: { "x-skip-loading-overlay": "true" },
        } as AxiosRequestConfig
      );
      const token = refreshed.data.accessToken;
      if (typeof token !== "string" || !token)
        throw new Error("認証応答を確認できません");
      const profile = await httpDao.get<UserInfo>("/me", {
        timeout: 15000,
        _staffRecovery: { revision, token },
      } as AxiosRequestConfig);
      return localAuthRepository.restoreStaffSession(
        token,
        profile.data,
        revision
      )
        ? profile.data
        : null;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        (error.response?.status === 401 ||
          (error.response?.status === 409 &&
            error.response.data?.code === "STAFF_SESSION_CHANGED"))
      )
        return null;
      // 通信障害ではCookie・他タブ・旧操作を変更せず、ユーザーが再試行する。
      throw error;
    }
  })().finally(() => staffRecoveries.delete(revision));
  staffRecoveries.set(revision, recovery);
  return recovery;
}
export function refreshStaffAccessToken(): Promise<string> {
  if (!staffRefreshPromise) {
    if (!localAuthRepository.isStaffSessionCurrent())
      return Promise.reject(stale());
    const previous = localAuthRepository.getStaffToken();
    const revision = localAuthRepository.getStaffSessionRevision();
    staffRefreshPromise = httpDao
      .post<{ accessToken: string }>("/auth/staff/refresh", {
        expectedStaffUserId: localAuthRepository.getStaffRecoveryIdentity()?.id,
      })
      .then((response) => {
        if (
          localAuthRepository.getStaffToken() !== previous ||
          localAuthRepository.getStaffSessionRevision() !== revision
        )
          throw stale();
        if (
          typeof response.data.accessToken !== "string" ||
          !response.data.accessToken
        )
          throw new Error("認証応答を確認できません");
        if (
          !localAuthRepository.setStaffRefreshedToken(
            response.data.accessToken,
            revision
          )
        )
          throw stale();
        return response.data.accessToken;
      })
      .catch((error: unknown) => {
        if (
          axios.isAxiosError(error) &&
          error.response?.status === 409 &&
          error.response.data?.code === "STAFF_SESSION_CHANGED" &&
          localAuthRepository.getStaffSessionRevision() === revision &&
          localAuthRepository.getStaffToken() === previous
        )
          localAuthRepository.invalidateStaffTab();
        if (
          isUnauthorized(error) &&
          localAuthRepository.getStaffSessionRevision() === revision &&
          localAuthRepository.getStaffToken() === previous
        )
          clearStaffSession();
        throw error;
      })
      .finally(() => {
        staffRefreshPromise = null;
      });
  }
  return staffRefreshPromise;
}
const memberRefreshes = new Map<string, Promise<void>>();
function refreshMember(session: MemberSession, revision: string) {
  let promise = memberRefreshes.get(revision);
  if (!promise) {
    promise = httpDao
      .post<unknown>("/auth/member/refresh", {
        refreshToken: session.refreshToken,
      })
      .then((response) => {
        if (
          !localAuthRepository.member.refresh(response.data, session, revision)
        )
          throw stale();
      })
      .catch((error: unknown) => {
        if (isUnauthorized(error)) localAuthRepository.member.clear(revision);
        throw error;
      })
      .finally(() => {
        memberRefreshes.delete(revision);
      });
    memberRefreshes.set(revision, promise);
  }
  return promise;
}

httpDao.interceptors.request.use(async (original) => {
  const config: RequestConfig = original;
  const scope = httpAuthScope(config.url);
  if (scope === "member") {
    await ensureMemberSession();
    const state = localAuthRepository.member.read();
    if (
      "_memberRevision" in config &&
      state.revision !== config._memberRevision
    )
      throw stale();
    config._memberRevision = state.revision;
    config.withCredentials = false;
    config.headers.delete("Authorization");
    if (state.session)
      config.headers.set(
        "Authorization",
        "Bearer " + state.session.accessToken
      );
    // Cancel transport and reject stale adapter responses that cannot be canceled.
    const controller = new AbortController();
    const previousSignal = config.signal;
    const abort = () => controller.abort();
    const changed = () => {
      if (localAuthRepository.member.read().revision !== state.revision)
        abort();
    };
    if (previousSignal?.aborted) abort();
    previousSignal?.addEventListener?.("abort", abort);
    if (typeof window !== "undefined")
      window.addEventListener(memberSessionChangedEvent, changed);
    config.signal = controller.signal;
    config._memberCleanup = () => {
      previousSignal?.removeEventListener?.("abort", abort);
      if (typeof window !== "undefined")
        window.removeEventListener(memberSessionChangedEvent, changed);
    };
  } else if (scope === "public") {
    config.withCredentials = false;
    config.headers.delete("Authorization");
  } else {
    const recovery = config._staffRecovery;
    if (recovery) {
      const recoveryPath = pathname(config.url);
      if (
        scope !== "staff" ||
        recovery.revision !== localAuthRepository.getStaffSessionRevision() ||
        !(
          (recoveryPath === "/auth/staff/refresh" &&
            config.method === "post" &&
            !recovery.token) ||
          (recoveryPath === "/me" && config.method === "get" && recovery.token)
        )
      )
        throw stale();
    }
    const publicStaffAuth =
      /^\/auth\/staff\/(login|password-reset|2fa)(?:\/|$)/.test(
        pathname(config.url)
      );
    if (
      !recovery &&
      !publicStaffAuth &&
      !localAuthRepository.isStaffSessionCurrent()
    )
      throw stale();
    if (
      config._staffRevision &&
      config._staffRevision !== localAuthRepository.getStaffSessionRevision()
    )
      throw stale();
    config._staffRevision = localAuthRepository.getStaffSessionRevision();
    const token =
      recovery?.token ??
      (recovery ? null : localAuthRepository.getStaffToken());
    config.headers.delete("Authorization");
    if (token) config.headers.set("Authorization", "Bearer " + token);
  }
  const skip = Boolean(config.headers["x-skip-loading-overlay"]);
  config.headers.delete("x-skip-loading-overlay");
  if (/^(post|put|patch|delete)$/i.test(config.method ?? "") && !skip) {
    activeMutationCount++;
    if (activeMutationCount === 1) dispatch("app:navigation-start");
    config._isMutationTracked = true;
  }
  return config;
});

httpDao.interceptors.response.use(
  (response) => {
    const config: RequestConfig = response.config;
    finish(config);
    if (
      config._staffRevision &&
      config._staffRevision !== localAuthRepository.getStaffSessionRevision()
    )
      throw stale();
    if (
      "_memberRevision" in config &&
      localAuthRepository.member.read().revision !== config._memberRevision
    )
      throw stale();
    return response;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) throw error;
    const config: RequestConfig | undefined = error.config;
    finish(config);
    if (!config) throw error;
    if (
      config._staffRevision &&
      config._staffRevision !== localAuthRepository.getStaffSessionRevision()
    )
      throw stale();
    const scope = httpAuthScope(config.url);
    if (
      "_memberRevision" in config &&
      localAuthRepository.member.read().revision !== config._memberRevision
    )
      throw stale();
    if (
      config._staffRecovery ||
      isAuthRequest(config.url) ||
      config._retry ||
      scope === "public"
    )
      throw error;
    if (!isUnauthorized(error)) throw error;
    config._retry = true;
    if (scope === "member") {
      const state = localAuthRepository.member.read();
      if (!state.session || !state.revision) throw error;
      // A sibling request may already have rotated the same session.
      if (
        config.headers.get("Authorization") ===
        "Bearer " + state.session.accessToken
      )
        await refreshMember(state.session, state.revision);
    } else {
      await refreshStaffAccessToken();
      // A fresh tab joins the current Cookie session, without creating a login generation.
      config._staffRevision = localAuthRepository.getStaffSessionRevision();
    }
    return httpDao.request(config);
  }
);
export default httpDao;
